import React, { useEffect, useRef } from "react";
import formatCurrency from "@tvg/formatter/currency";
import { animated, config, useTransition } from "@react-spring/web";
import { Paragraph } from "@tvg/design-system";
import {
  Button,
  Container,
  List,
  ListItem,
  WebScrollbarStyled
} from "./styled-components";

export interface DropdownProps {
  onSelectValue: (value: string) => void;
  values: Array<string>;
  selectedValue: string;
  isVisible: boolean;
  onClickAway: () => void;
}

const AnimatedContainer = animated(Container);

const Dropdown = ({
  onSelectValue,
  values,
  selectedValue,
  isVisible,
  onClickAway
}: DropdownProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const transition = useTransition(isVisible, {
    config: isVisible ? { ...config.stiff } : { duration: 150 },
    from: { opacity: 0, transform: `translate3d(0px, 20px, 0px)` },
    enter: { opacity: 1, transform: `translate3d(0px, 0px, 0px)` },
    leave: { opacity: 0, transform: `translate3d(0px, 20px, 0px)` }
  });

  useEffect(() => {
    const container = document.getElementById("web-scroll-bar-container");
    const element = document.getElementById(`dropdown-button-${selectedValue}`);

    if (container && element) {
      const containerDOMRect = container.getBoundingClientRect();
      const elementDOMRect = element.getBoundingClientRect();
      const visibleArea = containerDOMRect.height - elementDOMRect.height;
      const desiredScrollPosition =
        elementDOMRect.top - containerDOMRect.top - visibleArea;

      if (desiredScrollPosition > 0) {
        container.scrollTo({
          top: desiredScrollPosition,
          behavior: "smooth"
        });
      }
    }
  }, [isVisible]);

  useEffect(() => {
    // @ts-ignore
    const handleClickAway = (event) => {
      const isSelectorButton =
        event.target.getAttribute("data-qa-label") &&
        event.target.getAttribute("data-qa-label").toString() ===
          "bet-slip-amount-selector-button";
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !isSelectorButton
      ) {
        onClickAway();
      }
    };
    document.addEventListener("click", handleClickAway, true);
    return () => {
      document.removeEventListener("click", handleClickAway, true);
    };
  }, [ref, onClickAway]);

  return (
    <>
      {transition(
        (styles, isOpen) =>
          isOpen &&
          values.length > 0 && (
            <AnimatedContainer
              data-qa-label="bet-slip-amount-selector-dropdown"
              ref={ref}
              style={styles}
            >
              <WebScrollbarStyled
                height="294px"
                width="170px"
                scrollOrientation="vertical"
                shadowPadding="0"
                useShadow
              >
                <List>
                  {values.map((item, index) => (
                    <ListItem key={index.toString()}>
                      <Button
                        onClick={() => {
                          onSelectValue(item);
                        }}
                        data-qa-label={`dropdown-button-${item}`}
                        id={`dropdown-button-${item}`}
                        isActive={selectedValue === item}
                      >
                        <Paragraph
                          fontSize="s"
                          color="grey.900"
                          qaLabel="dropdown-button-text"
                        >
                          {index !== values.length - 1 && !Number.isNaN(item)
                            ? formatCurrency(+item)
                            : item}
                        </Paragraph>
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </WebScrollbarStyled>
            </AnimatedContainer>
          )
      )}
    </>
  );
};

export default Dropdown;
